import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Loader2, X, Eye, Clock, RefreshCw } from "lucide-react";

/**
 * Email Proposal dialog.
 *
 * Renders a compose modal that sends the multi-page proposal PDF
 * (server-side Playwright render of /quotes/{id}/print) to the client
 * via Resend. On success the quote moves to "sent" status and the
 * parent is notified so it can refresh in place.
 */
export default function EmailProposalDialog({
  open,
  onOpenChange,
  quote,
  client,
  settings,
  onSent,
}) {
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [markAsSent, setMarkAsSent] = useState(true);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);

  // Per-recipient send/open status — every address emailed so far has a
  // tracked link in recipient_links; opens are attributed in view_events.
  const recipientStatus = useMemo(() => {
    const opened = {};
    for (const ev of quote?.view_events || []) {
      const key = (ev.recipient || "").toLowerCase();
      if (key && (!opened[key] || ev.at > opened[key].at)) {
        opened[key] = { at: ev.at, location: ev.location || "" };
      }
    }
    return (quote?.recipient_links || []).map((r) => {
      const key = (r.email || "").toLowerCase();
      return { email: r.email, opened: opened[key] || null };
    });
  }, [quote]);
  const unopened = recipientStatus.filter((r) => !r.opened).map((r) => r.email);

  const firstName = useMemo(() => {
    const full =
      client?.primary_contact?.full_name || client?.name || "";
    return (full || "there").trim().split(/\s+/)[0];
  }, [client]);

  // Prefill on open — reset every time so switching between quotes
  // doesn't leak state from a previous open.
  useEffect(() => {
    if (!open || !quote) return;
    const primaryEmail = client?.primary_contact?.email || client?.email || "";
    const secondaryEmails = (client?.contacts || [])
      .filter((c) => c && c.id !== client?.primary_contact_id && c.email)
      .map((c) => c.email);
    setToList(primaryEmail ? [primaryEmail] : []);
    setCcList(secondaryEmails);
    setBccList([]);
    const vessel = quote.vessel?.name ? ` — ${quote.vessel.name}` : "";
    setSubject(
      `Proposal${vessel} — ${quote.number || quote.title || "Southern Ocean Solutions"}`,
    );
    const intro = (quote.intro_letter || "").trim();
    const defaultBody = intro
      ? intro
      : `Dear ${firstName},\n\nPlease find our proposal attached for your review. We'd be delighted to walk you through any of the details when convenient.\n\nKind regards,\n${settings?.company_name || "Southern Ocean Solutions"}`;
    setBody(defaultBody);
    setMarkAsSent(true);
  }, [open, quote, client, settings, firstName]);

  const send = async () => {
    if (!toList.length) {
      toast.error("Add at least one recipient in To");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!body.trim()) {
      toast.error("Message body is required");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/quotes/${quote.id}/email-proposal`, {
        to: toList,
        cc: ccList.length ? ccList : null,
        bcc: bccList.length ? bccList : null,
        subject: subject.trim(),
        message: body,
        mark_as_sent: markAsSent,
      });
      toast.success(
        `Proposal emailed to ${toList.join(", ")}${markAsSent ? " · status updated to Sent" : ""}`,
      );
      onSent?.(res.data?.quote || null);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const resendUnopened = async () => {
    if (!unopened.length) return;
    setResending(true);
    try {
      const res = await api.post(`/quotes/${quote.id}/email-proposal`, {
        to: unopened,
        subject: subject.trim() || `Proposal — ${quote.number || quote.title || ""}`.trim(),
        message: body,
        mark_as_sent: false,
      });
      toast.success(`Re-sent to ${unopened.join(", ")}`);
      onSent?.(res.data?.quote || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#C4D0DB] text-[#011836] max-w-2xl"
        data-testid="email-proposal-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#011836] flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#0088CC]" />
            Email proposal to client
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {recipientStatus.length > 0 && (
            <div className="rounded-md border border-[#C4D0DB] bg-white p-3" data-testid="recipient-status-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">
                  Previously sent — open status
                </div>
                {unopened.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={resendUnopened}
                    disabled={resending || sending}
                    className="border-[#F59E0B]/50 text-amber-700 hover:bg-amber-500/10 h-7 text-xs"
                    data-testid="resend-unopened-btn"
                  >
                    {resending ? (
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3 mr-1.5" />
                    )}
                    Resend to unopened ({unopened.length})
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                {recipientStatus.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-xs" data-testid={`recipient-status-${i}`}>
                    <span className="text-[#011836] truncate">{r.email}</span>
                    {r.opened ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 whitespace-nowrap">
                        <Eye className="h-3 w-3" />
                        Opened {new Date(r.opened.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {r.opened.location ? ` · ${r.opened.location}` : ""}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-400/90 whitespace-nowrap">
                        <Clock className="h-3 w-3" /> Not opened
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <EmailChipInput
            label="To"
            values={toList}
            onChange={setToList}
            placeholder="client@example.com"
            testid="email-to"
          />
          <EmailChipInput
            label="CC"
            values={ccList}
            onChange={setCcList}
            placeholder="cc@example.com"
            testid="email-cc"
          />
          <EmailChipInput
            label="BCC"
            values={bccList}
            onChange={setBccList}
            placeholder="bcc@example.com"
            testid="email-bcc"
          />

          <div>
            <Label className="text-xs text-[#5B6B7C] uppercase tracking-widest">
              Subject
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-white border-[#C4D0DB] text-[#011836] mt-1"
              data-testid="email-subject"
            />
          </div>

          <div>
            <Label className="text-xs text-[#5B6B7C] uppercase tracking-widest">
              Message
            </Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="bg-white border-[#C4D0DB] text-[#011836] mt-1 min-h-[200px]"
              rows={10}
              data-testid="email-body"
            />
            <div className="text-[11px] text-[#64748B] mt-1">
              A &ldquo;View proposal&rdquo; button linking to a shareable
              live proposal page is auto-appended to the email. Line breaks in
              your message above are preserved.
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#5B6B7C] cursor-pointer">
            <input
              type="checkbox"
              checked={markAsSent}
              onChange={(e) => setMarkAsSent(e.target.checked)}
              className="accent-[#0088CC]"
              data-testid="email-mark-sent"
            />
            Mark this quote as <span className="text-[#011836] font-semibold">Sent</span> after emailing
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#C4D0DB]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            data-testid="email-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || !toList.length}
            className="bg-[#0088CC] hover:bg-[#006DA6] text-white"
            data-testid="email-send-btn"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send proposal
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmailChipInput({ label, values, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const commit = () => {
    const v = input.trim().replace(/[,;]$/, "");
    if (!v) return;
    if (!/.+@.+\..+/.test(v)) {
      toast.error(`"${v}" isn't a valid email`);
      return;
    }
    if (!values.includes(v)) onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <Label className="text-xs text-[#5B6B7C] uppercase tracking-widest">
        {label}
      </Label>
      <div className="mt-1 flex flex-wrap gap-1.5 p-2 rounded-md bg-white border border-[#C4D0DB] min-h-[42px]">
        {values.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 bg-[#0088CC]/15 text-[#8ec9ea] text-xs px-2 py-0.5 rounded"
            data-testid={`${testid}-chip`}
          >
            {email}
            <button
              type="button"
              onClick={() => onChange(values.filter((v) => v !== email))}
              className="hover:text-[#011836]"
              aria-label={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
              if (input.trim()) {
                e.preventDefault();
                commit();
              }
            } else if (e.key === "Backspace" && !input && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => input && commit()}
          placeholder={values.length ? "" : placeholder}
          className="flex-1 min-w-[140px] bg-transparent border-0 outline-none text-sm text-[#011836] placeholder:text-[#475569]"
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}
